// File: utils/dataLoader.js

/**
 * Data Loader
 *
 * Functions for loading regulation data from JSON files.
 * Handles caching, error handling, and fallbacks.
 */

import { DATA_PATHS, ERROR_MESSAGES } from './constants.js';

// Every regulation part with served data under public/data/<code>/. Used to
// scope cross-regulation search and preloading when no single part is chosen.
export const ALL_REGULATION_CODES = [
  '1910', '1926', '1902', '1903', '1904', '1905',
  '1915', '1917', '1918', '1919', '1928',
];

// ========================================
// Cache
// ========================================

const cache = {
  regulations: null,
  indexes: {},
  sections: {},
};

// ========================================
// Core Loading Functions
// ========================================

/**
 * Load JSON file with error handling
 */
async function loadJSON(url) {
  try {
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    return { success: true, data };
    
  } catch (error) {
    console.error(`Error loading ${url}:`, error);
    return { 
      success: false, 
      error: error.message,
      message: ERROR_MESSAGES.NETWORK_ERROR 
    };
  }
}

/**
 * Load with cache support
 */
async function loadWithCache(url, cacheKey = null) {
  // Check cache first
  if (cacheKey && cache[cacheKey]) {
    return { success: true, data: cache[cacheKey], cached: true };
  }
  
  // Load from network
  const result = await loadJSON(url);
  
  // Cache successful results
  if (result.success && cacheKey) {
    if (cacheKey.startsWith('section_')) {
      cache.sections[cacheKey] = result.data;
    } else if (cacheKey.startsWith('index_')) {
      cache.indexes[cacheKey] = result.data;
    } else {
      cache[cacheKey] = result.data;
    }
  }
  
  return result;
}

// ========================================
// Regulation List
// ========================================

/**
 * Load list of all regulations
 */
export async function loadRegulations() {
  const result = await loadWithCache(DATA_PATHS.REGULATIONS, 'regulations');
  
  if (result.success) {
    return {
      success: true,
      regulations: result.data.regulations,
      metadata: result.data.metadata,
      cached: result.cached || false
    };
  }
  
  return result;
}

/**
 * Get regulation by code
 */
export async function getRegulation(code) {
  const result = await loadRegulations();
  
  if (!result.success) {
    return result;
  }
  
  const regulation = result.regulations.find(r => r.code === code);
  
  if (!regulation) {
    return {
      success: false,
      error: `Regulation ${code} not found`,
      message: ERROR_MESSAGES.SECTION_NOT_FOUND
    };
  }
  
  return {
    success: true,
    regulation
  };
}

// ========================================
// Regulation Index (Subparts & Sections)
// ========================================

/**
 * Load regulation index (structure with subparts)
 */
export async function loadRegulationIndex(code, lang = 'en') {
  // Localized navigation index (index.es.json / index.ar.json) when a
  // non-English language is selected, falling back to the English index
  // if the localized file isn't available.
  const baseUrl = DATA_PATHS.REGULATION_INDEX(code);
  const url = lang && lang !== 'en'
    ? baseUrl.replace(/index\.json$/, `index.${lang}.json`)
    : baseUrl;
  const cacheKey = `index_${code}_${lang || 'en'}`;

  let result = await loadWithCache(url, cacheKey);
  if (!result.success && url !== baseUrl) {
    result = await loadWithCache(baseUrl, `index_${code}_en`);
  }

  if (result.success) {
    return {
      success: true,
      regulation: result.data.regulation,
      subparts: result.data.subparts,
      metadata: result.data.metadata,
      cached: result.cached || false
    };
  }

  return result;
}

/**
 * Get subpart by letter
 */
export async function getSubpart(code, letter) {
  const result = await loadRegulationIndex(code);
  
  if (!result.success) {
    return result;
  }
  
  const subpart = result.subparts.find(s => s.letter === letter.toUpperCase());
  
  if (!subpart) {
    return {
      success: false,
      error: `Subpart ${letter} not found in ${code}`,
      message: ERROR_MESSAGES.SECTION_NOT_FOUND
    };
  }
  
  return {
    success: true,
    subpart
  };
}

/**
 * Search for section in index
 */
export async function findSectionInIndex(code, sectionNumber) {
  const result = await loadRegulationIndex(code);
  
  if (!result.success) {
    return result;
  }
  
  // Search through all subparts
  for (const subpart of result.subparts) {
    const section = subpart.sections.find(s => s.code === `${code}.${sectionNumber}`);
    if (section) {
      return {
        success: true,
        section,
        subpart: {
          letter: subpart.letter,
          name: subpart.name
        }
      };
    }
  }
  
  return {
    success: false,
    error: `Section ${code}.${sectionNumber} not found`,
    message: ERROR_MESSAGES.SECTION_NOT_FOUND
  };
}

// ========================================
// Section Content
// ========================================

/**
 * Load section content
 */
export async function loadSection(code, sectionNumber) {
  // First, validate section exists in index
  const indexResult = await findSectionInIndex(code, sectionNumber);
  
  if (!indexResult.success) {
    return indexResult;
  }
  
  // Load full section content
  const url = DATA_PATHS.SECTION(code, `${code}.${sectionNumber}`);
  const cacheKey = `section_${code}_${sectionNumber}`;
  
  const result = await loadWithCache(url, cacheKey);
  
  if (result.success) {
    return {
      success: true,
      section: result.data,
      subpart: indexResult.subpart,
      cached: result.cached || false
    };
  }
  
  return result;
}

/**
 * Load a translated version of a section (e.g. Spanish).
 *
 * Translation files live next to the English source as
 * /data/{reg}/sections/{code}.{lang}.json and are generated by
 * scripts/translateData.js. Missing translations are expected for
 * sections that haven't been translated yet, so a 404 is reported
 * via `notFound` rather than logged as an error.
 */
export async function loadSectionTranslation(code, sectionNumber, language = 'es') {
  const fullCode = `${code}.${sectionNumber}`;
  const cacheKey = `section_${code}_${sectionNumber}_${language}`;

  if (cache.sections[cacheKey]) {
    return { success: true, translation: cache.sections[cacheKey], cached: true };
  }

  const url = DATA_PATHS.SECTION(code, fullCode).replace(/\.json$/, `.${language}.json`);

  try {
    const response = await fetch(url);

    if (!response.ok) {
      return {
        success: false,
        notFound: response.status === 404,
        error: `HTTP ${response.status}`,
      };
    }

    const data = await response.json();
    cache.sections[cacheKey] = data;
    return { success: true, translation: data };
  } catch (error) {
    return { success: false, notFound: false, error: error.message };
  }
}

/**
 * Load multiple sections
 */
export async function loadSections(sections) {
  const results = await Promise.allSettled(
    sections.map(({ code, sectionNumber }) => loadSection(code, sectionNumber))
  );
  
  return results.map((result, index) => {
    if (result.status === 'fulfilled') {
      return result.value;
    } else {
      return {
        success: false,
        error: result.reason.message,
        section: sections[index]
      };
    }
  });
}

// ========================================
// Search Functions
// ========================================

/**
 * Search sections by keyword
 */
export async function searchSections(query, regulationCode = null) {
  if (!query || query.trim().length < 2) {
    return {
      success: false,
      error: 'Query too short',
      message: 'Please enter at least 2 characters'
    };
  }
  
  const normalizedQuery = query.toLowerCase().trim();
  const results = [];
  
  // Determine which regulations to search
  const codes = regulationCode ? [regulationCode] : ALL_REGULATION_CODES;
  
  for (const code of codes) {
    const indexResult = await loadRegulationIndex(code);
    
    if (!indexResult.success) continue;
    
    // Search through all subparts and sections
    for (const subpart of indexResult.subparts) {
      for (const section of subpart.sections) {
        // Search in code, title
        const searchableText = `${section.code} ${section.title}`.toLowerCase();
        
        if (searchableText.includes(normalizedQuery)) {
          results.push({
            ...section,
            regulation: code,
            subpart: {
              letter: subpart.letter,
              name: subpart.name
            },
            relevance: calculateRelevance(searchableText, normalizedQuery)
          });
        }
      }
    }
  }
  
  // Sort by relevance
  results.sort((a, b) => b.relevance - a.relevance);
  
  return {
    success: true,
    results,
    query,
    count: results.length
  };
}

/**
 * Calculate relevance score for search results
 */
function calculateRelevance(text, query) {
  let score = 0;
  
  // Exact match
  if (text === query) score += 100;
  
  // Starts with query
  if (text.startsWith(query)) score += 50;
  
  // Contains query
  if (text.includes(query)) score += 25;
  
  // Word boundary match
  const words = text.split(/\s+/);
  if (words.some(word => word.startsWith(query))) score += 15;
  
  return score;
}

/**
 * Search by exact section code
 */
export async function searchByCode(fullCode) {
  // Parse code like "1910.22" or "1926.501"
  const match = fullCode.match(/^(\d{4})\.(\d+)([a-z])?$/);
  
  if (!match) {
    return {
      success: false,
      error: 'Invalid section code format',
      message: ERROR_MESSAGES.INVALID_SECTION
    };
  }
  
  const [, regulation, section] = match;
  
  return await loadSection(regulation, section);
}

// ========================================
// Popular & Critical Sections
// ========================================

/**
 * Get popular sections for a regulation
 */
export async function getPopularSections(code) {
  const regResult = await getRegulation(code);
  
  if (!regResult.success) {
    return regResult;
  }
  
  const popularCodes = regResult.regulation.popular_sections || [];
  const sections = [];
  
  for (const fullCode of popularCodes) {
    const [, sectionNum] = fullCode.split('.');
    const result = await findSectionInIndex(code, sectionNum);
    if (result.success) {
      sections.push(result.section);
    }
  }
  
  return {
    success: true,
    sections
  };
}

/**
 * Get critical safety sections
 */
export async function getCriticalSections(code) {
  const regResult = await getRegulation(code);
  
  if (!regResult.success) {
    return regResult;
  }
  
  const criticalCodes = regResult.regulation.critical_safety || [];
  const sections = [];
  
  for (const fullCode of criticalCodes) {
    const [, sectionNum] = fullCode.split('.');
    const result = await findSectionInIndex(code, sectionNum);
    if (result.success) {
      sections.push({
        ...result.section,
        critical: true
      });
    }
  }
  
  return {
    success: true,
    sections
  };
}

// ========================================
// Cache Management
// ========================================

/**
 * Clear all cache
 */
export function clearCache() {
  cache.regulations = null;
  cache.indexes = {};
  cache.sections = {};
  return { success: true, message: 'Cache cleared' };
}

/**
 * Clear specific cache
 */
export function clearCacheFor(type, key = null) {
  if (type === 'regulations') {
    cache.regulations = null;
  } else if (type === 'indexes' && key) {
    delete cache.indexes[`index_${key}`];
  } else if (type === 'sections' && key) {
    delete cache.sections[`section_${key}`];
  } else if (type === 'indexes') {
    cache.indexes = {};
  } else if (type === 'sections') {
    cache.sections = {};
  }
  
  return { success: true };
}

/**
 * Get cache statistics
 */
export function getCacheStats() {
  return {
    regulations: cache.regulations ? 1 : 0,
    indexes: Object.keys(cache.indexes).length,
    sections: Object.keys(cache.sections).length,
    total: (cache.regulations ? 1 : 0) + 
           Object.keys(cache.indexes).length + 
           Object.keys(cache.sections).length
  };
}

// ========================================
// Preload Functions
// ========================================

/**
 * Preload common data
 */
export async function preloadCommonData() {
  const results = await Promise.allSettled([
    loadRegulations(),
    // Preload the two most-used indexes; the rest load lazily on demand.
    loadRegulationIndex('1910'),
    loadRegulationIndex('1926')
  ]);
  
  const successful = results.filter(r => r.status === 'fulfilled').length;
  
  return {
    success: true,
    loaded: successful,
    total: results.length,
    message: `Preloaded ${successful}/${results.length} resources`
  };
}

// ========================================
// Export All
// ========================================

const dataLoader = {
  // Regulations
  loadRegulations,
  getRegulation,
  
  // Index
  loadRegulationIndex,
  getSubpart,
  findSectionInIndex,
  
  // Sections
  loadSection,
  loadSections,
  loadSectionTranslation,
  
  // Search
  searchSections,
  searchByCode,
  
  // Popular/Critical
  getPopularSections,
  getCriticalSections,
  
  // Cache
  clearCache,
  clearCacheFor,
  getCacheStats,
  
  // Preload
  preloadCommonData,
};

export default dataLoader;
